import React, { useEffect, useState } from 'react';
import { GraphvizDiagramCard } from '../C4DiagramCard/GraphvizDiagramCard';
import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  makeStyles,
} from '@material-ui/core';
import {
  Command,
  C4DiagramModel,
  C4DiagramEntity,
  C4DiagramDeploymentNode,
  C4DiagramDeploymentNodeEntity,
  C4DiagramRelation,
} from '../C4DiagramCard/types';
import { useApi } from '@backstage/core-plugin-api';
import { modelApiRef } from '../../../services/ModelClient';
// @ts-ignore
import * as uuid from 'uuid';
import Alert from '@material-ui/lab/Alert';
import { DialogDelete, DialogResult, DialogSave } from '../../dialogs';
import { DialogAddDeployment } from './DialogAddDeployment';
import { DialogEditTechnology } from './DialogEditTechnology';

export interface C4DeploymentDiagramCardProps {
  model: C4DiagramModel;
  components?: C4DiagramEntity[];
  rescources?: C4DiagramDeploymentNode[];
  relations?: C4DiagramRelation[];
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    '& > * + *': {
      marginTop: theme.spacing(2),
    },
  },
}));

export const C4DeploymentDiagramCard = (
  props: C4DeploymentDiagramCardProps,
) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>();
  const [selectedEntity, setSelectedEntity] = useState<
    undefined | C4DiagramDeploymentNodeEntity
  >();
  const [openContextMenu, setOpenContextMenu] = useState(false);
  const [openDialogAddDeployment, setOpenDialogAddDeployment] = useState(false);
  const [openDialogEditTechnology, setOpenDialogEditTechnology] =
    useState(false);
  const [openDialogSave, setOpenDialogSave] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [openDialogResult, setOpenDialogResult] = useState(false);
  const [components, setComponents] = useState<Array<C4DiagramEntity>>([]);
  const [rescources, setRescources] = useState<Array<C4DiagramDeploymentNode>>(
    [],
  );
  const [model, setModel] = useState(props.model);
  const [originalModel] = useState(JSON.parse(JSON.stringify(model)));
  const modelApi = useApi(modelApiRef);
  const [edit, setEdit] = useState(false);
  const [commands] = useState<Command[]>([]);
  const [mergeRequests, setMergeRequests] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [technology, setTechnology] = useState<string>('');

  useEffect(() => {
    setModel(props.model);
  }, [props.model]);

  useEffect(() => {
    setComponents(props.components ? props.components : []);
  }, [props.components]);

  useEffect(() => {
    setRescources(props.rescources ? props.rescources : []);
  }, [props.rescources]);

  const classes = useStyles();

  return (
    <>
      <div className={classes.root}>
        {edit && (
          <Alert severity="warning">
            You are now in the editing mode. You will loose all your changes if
            you leave this tab without saving!
          </Alert>
        )}
        {!edit && (
          <ButtonGroup variant="outlined">
            <Button onClick={onEdit}>Edit</Button>
          </ButtonGroup>
        )}
        {edit && (
          <ButtonGroup variant="outlined">
            <Button onClick={onSave}>Save</Button>
            <Button onClick={onCancel}>Cancel</Button>
          </ButtonGroup>
        )}
      </div>
      <GraphvizDiagramCard
        model={model}
        openContextMenu={handleOpenContextMenu}
        edit={edit}
      />
      <Menu
        anchorEl={anchorEl}
        open={openContextMenu}
        onClose={handleCloseContextMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onContextMenu={e => {
          e.preventDefault();
          handleCloseContextMenu();
          return false;
        }}
      >
        {isCluster() && (
          <MenuItem onClick={handleOpenDialogAddDeployment}>
            Add deployment
          </MenuItem>
        )}
        {isContainer() && (
          <MenuItem onClick={handleOpenEditTechnology}>Technology</MenuItem>
        )}
        {isContainer() && (
          <MenuItem onClick={handleOpenDialogDelete}>Delete</MenuItem>
        )}
      </Menu>
      <DialogSave
        open={openDialogSave}
        onClose={() => setOpenDialogSave(false)}
        onSave={handleSaveDialogSave}
      ></DialogSave>
      <DialogDelete
        open={openDialogDelete}
        onNo={() => setOpenDialogDelete(false)}
        onYes={handleDialogDeleteYes}
      ></DialogDelete>
      <DialogResult
        open={openDialogResult}
        mergeRequests={mergeRequests}
        errorMessage={errorMessage}
        onClose={() => setOpenDialogResult(false)}
      ></DialogResult>
      <DialogAddDeployment
        open={openDialogAddDeployment}
        onClose={() => setOpenDialogAddDeployment(false)}
        onSave={handleSaveDialogAddDeployment}
        components={components}
        resources={rescources}
      ></DialogAddDeployment>
      <DialogEditTechnology
        open={openDialogEditTechnology}
        onClose={() => setOpenDialogEditTechnology(false)}
        onSave={handleSaveDialogEditTechnology}
        technology={technology}
      />
    </>
  );

  function isCluster() {
    return anchorEl?.classList.value.includes('cluster');
  }

  function isContainer() {
    return anchorEl?.classList.value.includes('node');
  }

  function onEdit() {
    setEdit(true);
  }

  function onCancel() {
    setEdit(false);
    setModel({ ...originalModel });
  }

  function onSave() {
    setOpenDialogSave(true);
  }

  function handleSaveDialogEditTechnology(technology: string) {
    setOpenDialogEditTechnology(false);
    const node = findDeploymentNodeForDeploymentNodeEntity(
      model.deploymentNodes!,
      anchorEl!.id,
    )!;
    // Add technology
    if (!node.technology && technology.trim() !== '') {
      const command: Command = {
        action: 'new',
        location: selectedEntity!.entity!.location!,
        technology: {
          id: selectedEntity!.entity!.id,
          text: technology,
        },
      };
      commands.push(command);
      if (!node.deploymentNodes) {
        node.deploymentNodes = [];
      }
      let innerDeploymentNode = node.deploymentNodes.find(
        it => it.text === technology,
      );
      if (!innerDeploymentNode) {
        innerDeploymentNode = {
          id: uuid.v4(),
          text: technology,
          technology: true,
          parent: node.id,
        };
        node.deploymentNodes?.push(innerDeploymentNode);
      }
      if (!innerDeploymentNode.entities) {
        innerDeploymentNode.entities = [];
      }
      node.entities = node.entities!.filter(it => it.id !== selectedEntity!.id);
      innerDeploymentNode.entities?.push(selectedEntity!);
    }
    // Delete technology
    else if (node.technology && technology.trim() === '') {
      const command: Command = {
        action: 'delete',
        location: selectedEntity!.entity!.location!,
        technology: {
          id: selectedEntity!.entity!.id,
        },
      };
      commands.push(command);
      const parent = findDeploymentNode(model.deploymentNodes!, node.parent!)!;
      node.entities = node.entities!.filter(it => it.id !== selectedEntity!.id);
      if (node.entities.length === 0) {
        parent.deploymentNodes = parent.deploymentNodes?.filter(
          it => it.id !== node.id,
        );
      }
      if (!parent.entities) {
        parent.entities = [];
      }
      parent.entities?.push(selectedEntity!);
    }
    // Change technology
    else if (node.technology && technology.trim() !== '') {
      const command: Command = {
        action: 'change',
        location: selectedEntity!.entity!.location!,
        technology: {
          id: selectedEntity!.entity!.id,
          text: technology,
        },
      };
      commands.push(command);
      const parent = findDeploymentNode(model.deploymentNodes!, node.parent!)!;
      node.entities = node.entities!.filter(it => it.id !== selectedEntity!.id);
      if (node.entities.length === 0) {
        parent.deploymentNodes = parent.deploymentNodes?.filter(
          it => it.id !== node.id,
        );
      }
      if (!parent.deploymentNodes) {
        parent.deploymentNodes = [];
      }
      let innerDeploymentNode = parent.deploymentNodes.find(
        it => it.text === technology,
      );
      if (!innerDeploymentNode) {
        innerDeploymentNode = {
          id: uuid.v4(),
          text: technology,
          technology: true,
          parent: parent.id,
        };
        parent.deploymentNodes?.push(innerDeploymentNode);
      }
      if (!innerDeploymentNode.entities) {
        innerDeploymentNode.entities = [];
      }
      innerDeploymentNode.entities?.push(selectedEntity!);
    }
  }

  function handleSaveDialogAddDeployment(
    componentId: string,
    resourceId: string,
  ) {
    setOpenDialogAddDeployment(false);
    const component = components.find(it => it.id === componentId)!;
    const resource = rescources.find(it => it.id === resourceId)!;
    const command: Command = {
      action: 'new',
      location: component!.location!,
      relation: {
        id: uuid.v4(),
        source: componentId,
        sourceType: component.type,
        target: resourceId,
        targetType: 'Resource',
      },
    };
    commands.push(command);
    const root = getRoot(resource);
    if (!model.deploymentNodes) {
      model.deploymentNodes = [];
    }
    if (!model.deploymentNodes.includes(root)) {
      model.deploymentNodes.push(root);
    }
    if (!resource.entities) {
      resource.entities = [];
    }
    const newComponent = {
      id: uuid.v4(),
      entity: component,
    };
    props.relations
      ?.filter(it => it.source === component.id)
      .forEach(rel => {
        findDeploymentNodeForEntities(
          model.deploymentNodes!,
          rel.target,
        ).forEach(target =>
          pushRelation({
            ...rel,
            source: newComponent.id,
            target: target.id,
          }),
        );
      });
    props.relations
      ?.filter(it => it.target === component.id)
      .forEach(rel => {
        findDeploymentNodeForEntities(
          model.deploymentNodes!,
          rel.source,
        ).forEach(source =>
          pushRelation({
            ...rel,
            source: source.id,
            target: newComponent.id,
          }),
        );
      });
    resource.entities.push(newComponent);
  }

  function handleDialogDeleteYes() {
    setOpenDialogDelete(false);
    const deploymentNode = findDeploymentNodeForDeploymentNodeEntity(
      model.deploymentNodes!,
      selectedEntity!.id,
    )!;
    const command: Command = {
      action: 'delete',
      location: selectedEntity!.entity.location!,
      relation: {
        id: uuid.v4(),
        source: selectedEntity!.entity.id,
        sourceType: selectedEntity!.entity.type,
        target: deploymentNode.id,
        targetType: 'Resource',
      },
    };
    commands.push(command);
    deploymentNode.entities = deploymentNode.entities?.filter(
      it => it.id !== selectedEntity?.id,
    );
  }

  function findDeploymentNodeForEntities(
    nodes: C4DiagramDeploymentNode[],
    id: string,
  ): C4DiagramDeploymentNodeEntity[] {
    const result: C4DiagramDeploymentNodeEntity[] = [];
    for (const node of nodes) {
      node.entities
        ?.filter(it => it.entity.id === id)
        .forEach(it => result.push(it));
      if (node.deploymentNodes) {
        findDeploymentNodeForEntities(node.deploymentNodes, id).forEach(it =>
          result.push(it),
        );
      }
    }
    return result;
  }

  function findDeploymentNodeForDeploymentNodeEntity(
    nodes: C4DiagramDeploymentNode[],
    id: string,
  ): C4DiagramDeploymentNode | undefined {
    for (const node of nodes) {
      let entity = node.entities?.find(it => it.id === id);
      if (entity) {
        return node;
      } else if (node.deploymentNodes) {
        const childNode = findDeploymentNodeForDeploymentNodeEntity(
          node.deploymentNodes,
          id,
        );
        if (childNode) {
          return childNode;
        }
      }
    }
    return undefined;
  }

  function findDeploymentNodeEntity(
    id: string,
  ): C4DiagramDeploymentNodeEntity | undefined {
    return findDeploymentNodeForDeploymentNodeEntity(
      model.deploymentNodes!,
      id,
    )?.entities?.find(it => it.id === id);
  }

  function findDeploymentNode(
    nodes: C4DiagramDeploymentNode[],
    id: string,
  ): C4DiagramDeploymentNode | undefined {
    if (nodes) {
      for (const node of nodes) {
        if (node.id === id) {
          return node;
        } else if (node.deploymentNodes) {
          const deploymentNode = findDeploymentNode(node.deploymentNodes!, id);
          if (deploymentNode) {
            return deploymentNode;
          }
        }
      }
    }
    return undefined;
  }

  function getRoot(node: C4DiagramDeploymentNode) {
    if (node.parent) {
      const parent = rescources.find(it => it.id === node.parent)!;
      return getRoot(parent);
    }
    return node;
  }

  function handleSaveDialogSave(
    branch: string | undefined,
    message: string | undefined,
  ) {
    setEdit(false);
    setOpenDialogSave(false);
    setMergeRequests([]);
    setErrorMessage(undefined);
    setOpenDialogResult(true);
    modelApi
      .postMergeCommands({
        commands: commands,
        branch: branch!,
        message: message!,
      })
      .then(result => {
        if (Array.isArray(result)) {
          setMergeRequests(result);
          setModel({ ...originalModel });
        } else {
          setErrorMessage(result);
        }
      });
  }

  function handleOpenContextMenu(e: HTMLElement) {
    setAnchorEl(e);
    setOpenContextMenu(true);
  }

  function handleOpenEditTechnology() {
    setOpenDialogEditTechnology(true);
    const entity = findDeploymentNodeEntity(anchorEl!.id);
    const node = findDeploymentNodeForDeploymentNodeEntity(
      model.deploymentNodes!,
      anchorEl!.id,
    );
    setSelectedEntity(entity);
    if (node?.technology) {
      setTechnology(node.text);
    } else {
      setTechnology('');
    }
    setOpenContextMenu(false);
  }

  function handleOpenDialogDelete() {
    setOpenDialogDelete(true);
    setSelectedEntity(findDeploymentNodeEntity(anchorEl!.id));
    setOpenContextMenu(false);
  }

  function handleOpenDialogAddDeployment() {
    setOpenDialogAddDeployment(true);
    setOpenContextMenu(false);
  }

  function handleCloseContextMenu() {
    setAnchorEl(null);
    setOpenContextMenu(false);
  }

  function pushRelation(relation: C4DiagramRelation) {
    if (
      !model.relations.find(
        it => it.source === relation.source && it.target == relation.target,
      )
    ) {
      model.relations.push(relation);
    }
  }
};
